/**
 * SortControl - Core Reusable Sort Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides sorting functionality for any resource type.
 */

'use client';

export interface SortOption<T = unknown> {
  label: string;
  value: string;
  orderBy: (item: T) => unknown;
  direction?: 'asc' | 'desc';
}

export interface SortControlProps<T = unknown> {
  /**
   * Available sort options
   */
  options: SortOption<T>[];
  /**
   * Current sort value
   */
  value?: string;
  /**
   * Callback when sort changes
   */
  onChange?: (value: string) => void;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Label text
   */
  label?: string;
}

/**
 * Core Sort Control Component
 * 
 * Provides dropdown for sorting options.
 * Themes can customize appearance via className overrides.
 */
export function SortControl<T = unknown>({
  options,
  value,
  onChange,
  className = '',
  label = 'Sort by',
}: SortControlProps<T>) {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label htmlFor="sort" className="text-sm font-medium text-gray-700 whitespace-nowrap">
        {label}:
      </label>
      <select
        id="sort"
        value={value || options[0]?.value}
        onChange={handleChange}
        className="
          block rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10
          text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
        "
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

